import { useEffect, useState, useRef, useCallback } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { toast } from 'sonner';

import { APIBaseChronos } from '@/api/hosts';
import useCompleteRelativityOAuth from '@/api/mutations/useCompleteRelativityOAuth';
import { BroadcastChannelNames, RelativityAuthMessageTypes, RelativityAuthMessages } from '@/types/broadcast-channels';

export function useRelativityAuth() {
  const authInfo = useAuthInfo();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  // Several instances can be mounted at once; only the one that started the flow completes it.
  const sessionKeyRef = useRef<string | null>(null);
  const { mutate: completeRelativityOAuth } = useCompleteRelativityOAuth();

  // Check auth status
  const checkAuthStatus = useCallback(async () => {
    try {
      const response = await fetch(`${APIBaseChronos}/oauth/relativity/status`, {
        headers: {
          Authorization: `Bearer ${await authInfo.tokens.getAccessToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to check auth status');
      }

      const data = await response.json();
      setIsAuthenticated(data.isAuthenticated);
    } catch (error) {
      console.error('Error checking Relativity auth status:', error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, [authInfo.tokens]);
  // Read through a ref so a token refresh doesn't tear down and reopen the broadcast channel.
  const checkAuthStatusRef = useRef(checkAuthStatus);
  checkAuthStatusRef.current = checkAuthStatus;

  // Listen for OAuth callback
  useEffect(() => {
    const channel = new BroadcastChannel(BroadcastChannelNames.RelativityAuth);
    broadcastChannelRef.current = channel;

    const handleBroadcastMessage = (event: MessageEvent<RelativityAuthMessages>) => {
      if (event.data.type === RelativityAuthMessageTypes.Connected) {
        checkAuthStatusRef.current();
        return;
      }

      const sessionKey = sessionKeyRef.current;
      if (!sessionKey || event.data.sessionKey !== sessionKey) {
        return;
      }

      sessionKeyRef.current = null;

      if (event.data.type === RelativityAuthMessageTypes.Success) {
        completeRelativityOAuth(
          { state: sessionKey, code: event.data.code },
          {
            onSuccess: () => {
              toast.success('Successfully connected to Relativity');
              setIsAuthenticated(true);
              broadcastChannelRef.current?.postMessage({
                type: RelativityAuthMessageTypes.Connected,
                sessionKey: null,
                timestamp: Date.now(),
              } satisfies RelativityAuthMessages);
            },
            onError: (error) => {
              toast.error(`Failed to connect to Relativity: ${error.message}`);
            },
            onSettled: () => setIsConnecting(false),
          },
        );
      } else if (event.data.type === RelativityAuthMessageTypes.Error) {
        toast.error(`Failed to connect to Relativity: ${event.data.description || event.data.error}`);
        setIsConnecting(false);
      }
    };

    channel.addEventListener('message', handleBroadcastMessage);

    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
    };
  }, [completeRelativityOAuth]);

  // Initial auth check
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Connect to Relativity
  const connectRelativity = async () => {
    setIsConnecting(true);
    try {
      const response = await fetch(`${APIBaseChronos}/oauth/relativity/start`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await authInfo.tokens.getAccessToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to start OAuth flow');
      }

      const data = await response.json();
      sessionKeyRef.current = new URL(data.authUrl).searchParams.get('state');
      window.open(data.authUrl, 'relativity-auth', 'width=800,height=600');
    } catch (error) {
      console.error('Error starting Relativity OAuth:', error);
      toast.error('Failed to start Relativity authentication');
      setIsConnecting(false);
    }
  };

  const disconnectRelativity = async () => {
    setIsDisconnecting(true);
    try {
      const response = await fetch(`${APIBaseChronos}/oauth/relativity/logout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await authInfo.tokens.getAccessToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect Relativity');
      }

      setIsAuthenticated(false);
      toast.success('Disconnected from Relativity');
    } catch (error) {
      console.error('Error disconnecting Relativity:', error);
      toast.error('Failed to disconnect Relativity');
    } finally {
      setIsDisconnecting(false);
    }
  };

  return {
    isAuthenticated,
    isLoading,
    isConnecting,
    isDisconnecting,
    connectRelativity,
    disconnectRelativity,
    refreshAuthStatus: checkAuthStatus,
  };
}
